import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { fmt } from "../../lib/api";
import SectionCard from "./SectionCard";
import { Button } from "../ui/button";
import { Plus, Trash2 } from "lucide-react";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../ui/select";

export default function Kategori4({ factors, data, setData, results }) {
  const setVal = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setGroup = (g, id, v) => setData((d) => ({ ...d, [g]: { ...(d[g] || {}), [id]: v } }));

  const items = data.satin_alinan_items || [];
  const setItems = (newItems) => setData((d) => ({ ...d, satin_alinan_items: newItems }));
  const addItem = () => setItems([...items, { label: "", malzeme_id: "sa_ozel", fv: "", custom_ef: "" }]);
  const updateItem = (i, k, v) => {
    const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n);
  };
  const removeItem = (i) => setItems(items.filter((_, x) => x !== i));

  return (
    <div className="space-y-6 animate-fadeInUp">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Kategori 4</div>
        <h2 className="font-display text-3xl font-semibold">Kuruluş Tarafından Kullanılan Ürünler</h2>
        <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
          Su, atıksu, atık bertarafı, satın alınan ürünler ve yakıt üretim emisyonları (WTT).
        </p>
      </div>

      <SectionCard title="Su & Atıksu" total={(results?.su?.total || 0) + (results?.atiksu?.total || 0)}>
        <div className="grid md:grid-cols-2 gap-6">
          <SimpleInput label="Su Tüketimi (m³)" factor={factors.su}
                       value={data.su || ""} onChange={(v) => setVal("su", v)} result={results?.su?.total} />
          <SimpleInput label="Atıksu Bertarafı (m³)" factor={factors.atiksu}
                       value={data.atiksu || ""} onChange={(v) => setVal("atiksu", v)} result={results?.atiksu?.total} />
        </div>
      </SectionCard>

      <SectionCard title="Atık Bertarafı" total={results?.atik_total}>
        <TableHeader />
        {factors.atik.map((f) => (
          <Row key={f.id} factor={f}
               value={data.atik?.[f.id] || ""}
               onChange={(v) => setGroup("atik", f.id, v)}
               result={results?.atik?.find((x) => x.key === f.id)?.total} />
        ))}
      </SectionCard>

      <SectionCard title="Satın Alınan Ürünler" total={results?.satin_alinan_total}>
        <div className="text-xs text-muted-foreground mb-3">
          Ürünlerinizi ekleyin. "Özel" seçerseniz EF'i manuel girebilirsiniz.
        </div>
        {items.length === 0 && (
          <div className="text-sm text-muted-foreground italic py-3">Henüz ürün eklenmedi.</div>
        )}
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center py-2 border-t border-border/50">
            <Input className="col-span-3 h-9" placeholder="Ürün adı"
                   value={it.label} onChange={(e) => updateItem(i, "label", e.target.value)}
                   data-testid={`k4-sa-label-${i}`} />
            <div className="col-span-3">
              <Select value={it.malzeme_id} onValueChange={(v) => updateItem(i, "malzeme_id", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {factors.satin_alinan.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input type="number" step="any" className="col-span-2 h-9" placeholder="ton"
                   value={it.fv} onChange={(e) => updateItem(i, "fv", e.target.value)} />
            {it.malzeme_id === "sa_ozel" ? (
              <Input type="number" step="any" className="col-span-2 h-9" placeholder="EF (kg/ton)"
                     value={it.custom_ef} onChange={(e) => updateItem(i, "custom_ef", e.target.value)} />
            ) : (
              <div className="col-span-2 text-xs text-muted-foreground text-center">
                {fmt(factors.satin_alinan.find((m) => m.id === it.malzeme_id)?.ef, 2)}
              </div>
            )}
            <div className="col-span-1 text-right text-sm tabular-nums">
              {fmt(results?.satin_alinan?.[i]?.total || 0, 3)}
            </div>
            <div className="col-span-1 text-right">
              <Button size="icon" variant="ghost" onClick={() => removeItem(i)}
                      className="text-destructive h-8 w-8"><Trash2 className="size-3.5" /></Button>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addItem} className="mt-3 gap-2" data-testid="k4-sa-add-btn">
          <Plus className="size-4" /> Ürün Ekle
        </Button>
      </SectionCard>

      <SectionCard title="Kullanılan Yakıtın Üretimi (WTT - Well to Tank)" total={results?.yakit_total}>
        <div className="text-xs text-muted-foreground mb-3 p-3 bg-accent/10 border border-accent/30 rounded-lg">
          <span className="font-medium text-foreground">💡 Otomatik Doldurma:</span> Kategori 1'de girdiğiniz yakıt tüketimleri (motorin, benzin, doğalgaz, LPG…) buraya otomatik aktarılır. Manuel değer girerseniz o baskın olur.
        </div>
        <TableHeader />
        {factors.yakit_uretimi.map((f) => {
          const res = results?.yakit_uretimi?.find((x) => x.key === f.id);
          const autoFilled = res?.auto_filled;
          const manualVal = data.yakit_uretimi?.[f.id] || "";
          return (
            <div key={f.id} className="grid grid-cols-12 gap-2 items-center py-1.5 border-t border-border/50">
              <div className="col-span-6">
                <div className="font-medium text-sm flex items-center gap-2">
                  {f.label}
                  {autoFilled && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-semibold">
                      K1'den otomatik
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{f.kaynak}</div>
              </div>
              <div className="col-span-2">
                <Input type="number" step="any" value={manualVal}
                       onChange={(e) => setGroup("yakit_uretimi", f.id, e.target.value)}
                       placeholder={autoFilled ? `Oto: ${fmt(res.fv, 2)}` : `0 ${f.unit}`}
                       className={`h-9 ${autoFilled ? "border-primary/30 bg-primary/5" : ""}`}
                       data-testid={`input-${f.id}`} />
              </div>
              <div className="col-span-2 text-xs text-muted-foreground text-center tabular-nums">{fmt(f.ef, 4)}</div>
              <div className="col-span-2 text-right text-sm font-medium tabular-nums">
                {(res?.total || 0) > 0 ? fmt(res.total, 3) : "—"}
              </div>
            </div>
          );
        })}
      </SectionCard>
    </div>
  );
}

function SimpleInput({ label, factor, value, onChange, result }) {
  return (
    <div>
      <Label className="text-sm font-medium mb-1.5 block">{label}</Label>
      <Input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={`0 ${factor.unit}`} data-testid={`input-${factor.id}`} />
      <div className="text-[11px] text-muted-foreground mt-1.5 flex justify-between">
        <span>EF: {fmt(factor.ef, 4)} kg/{factor.unit}</span>
        <span className="font-medium text-foreground">{fmt(result || 0, 3)} tCO₂e</span>
      </div>
    </div>
  );
}

function TableHeader() {
  return (
    <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-muted-foreground px-1 pb-2">
      <div className="col-span-6">Kaynak</div>
      <div className="col-span-2">Faaliyet Verisi</div>
      <div className="col-span-2 text-center">EF</div>
      <div className="col-span-2 text-right">tCO₂e</div>
    </div>
  );
}

function Row({ factor, value, onChange, result }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1.5 border-t border-border/50">
      <div className="col-span-6">
        <div className="font-medium text-sm">{factor.label}</div>
        <div className="text-[11px] text-muted-foreground truncate">{factor.kaynak}</div>
      </div>
      <div className="col-span-2">
        <Input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)}
               placeholder={`0 ${factor.unit}`} className="h-9" data-testid={`input-${factor.id}`} />
      </div>
      <div className="col-span-2 text-xs text-muted-foreground text-center tabular-nums">{fmt(factor.ef, 4)}</div>
      <div className="col-span-2 text-right text-sm font-medium tabular-nums">
        {(result || 0) > 0 ? fmt(result, 3) : "—"}
      </div>
    </div>
  );
}
